import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, btnStyle, labelStyle } from '../theme';
import { Shield, Trash2, AlertTriangle, Wrench, Plus, X, RefreshCw, FileWarning, CheckCircle } from 'lucide-react';

// ComplianceLogsPage — single page that hosts the four NABH compliance log
// modules introduced in Phase 1. Tabs share the same shape (list + add form)
// so staff can keep entries up to date without page-hopping. Top KPI strip
// pulls /compliance-summary so the user sees today's autoclave count, open
// adverse events, equipment due-for-service etc. at a glance.
const TABS = [
  { key: 'sterilization', label: 'Sterilization Log',     icon: Shield,         endpoint: 'sterilizationlogs' },
  { key: 'bmw',           label: 'Bio-Medical Waste',     icon: Trash2,         endpoint: 'biomedicalwastelogs' },
  { key: 'adverse',       label: 'Adverse Events',        icon: AlertTriangle,  endpoint: 'adverseevents' },
  { key: 'equipment',     label: 'Equipment Maintenance', icon: Wrench,         endpoint: 'equipmentmaintenances' },
];

export default function ComplianceLogsPage({ API_BASE }) {
  const [tab, setTab] = useState('sterilization');
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});

  const active = TABS.find(t => t.key === tab);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/compliance/${active.endpoint}`);
      setItems(r.data || []);
    } catch (e) { setItems([]); }
  }, [API_BASE, active.endpoint]);

  const loadSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/compliance/compliance-summary`);
      setSummary(r.data);
    } catch { setSummary(null); }
  }, [API_BASE]);

  useEffect(() => { load(); loadSummary(); setShowForm(false); setForm({}); }, [load, loadSummary]);

  const save = async () => {
    try {
      await axios.post(`${API_BASE}/compliance/${active.endpoint}`, form);
      setShowForm(false); setForm({}); load(); loadSummary();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API_BASE}/compliance/${active.endpoint}/${id}`);
      load(); loadSummary();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0, color: 'var(--color-text)' }}>📋 Compliance Logs</h2>
        <button onClick={() => { load(); loadSummary(); }} style={{ ...btnStyle, marginTop: 0, padding: '8px 14px' }}>
          <RefreshCw size={14}/> Refresh
        </button>
      </div>

      {/* KPI summary strip */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 14 }}>
          <Kpi label="Sterilization Today" value={summary.sterilization?.today || 0} accent={theme.success} icon={Shield} />
          <Kpi label="Failed Cycles (30d)" value={summary.sterilization?.failed30d || 0} accent={theme.danger} icon={FileWarning} />
          <Kpi label="BMW Pickups (30d)" value={summary.bmw?.count30d || 0} accent={theme.info} icon={Trash2} />
          <Kpi label="Open Adverse Events" value={summary.adverseEvents?.openCount || 0} accent={theme.warning} icon={AlertTriangle} />
          <Kpi label="Sentinel Open" value={summary.adverseEvents?.sentinelOpen || 0} accent={theme.danger} icon={AlertTriangle} />
          <Kpi label="Equipment Due 7d" value={summary.equipment?.dueWithin7Days || 0} accent={theme.warning} icon={Wrench} />
          <Kpi label="Equipment Overdue" value={summary.equipment?.overdue || 0} accent={theme.danger} icon={Wrench} />
        </div>
      )}

      {/* Tab selector */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {TABS.map(t => {
          const Ic = t.icon;
          const a = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6,
                background: a ? 'var(--color-accent)' : 'var(--color-bg-inset)',
                color: a ? 'white' : 'var(--color-text)',
              }}>
              <Ic size={14}/> {t.label}
            </button>
          );
        })}
      </div>

      {/* Add button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        {!showForm && (
          <button onClick={() => setShowForm(true)} style={{ ...btnStyle, marginTop: 0, padding: '8px 16px' }}>
            <Plus size={14}/> Add {active.label}
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div style={{ ...cardStyle, marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <h3 style={{ margin: 0, color: 'var(--color-text)' }}>+ New {active.label}</h3>
            <button onClick={() => { setShowForm(false); setForm({}); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
              <X size={18}/>
            </button>
          </div>

          {tab === 'sterilization' && <SterilizationForm form={form} setForm={setForm} />}
          {tab === 'bmw'           && <BmwForm           form={form} setForm={setForm} />}
          {tab === 'adverse'       && <AdverseForm       form={form} setForm={setForm} />}
          {tab === 'equipment'     && <EquipmentForm     form={form} setForm={setForm} />}

          <button onClick={save} style={{ ...btnStyle, marginTop: 14 }}>Save</button>
        </div>
      )}

      {/* List */}
      <div style={{ ...cardStyle }}>
        {items.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
            No {active.label.toLowerCase()} entries yet. Click "Add" above to log the first one.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {tab === 'sterilization' && <>
                  <Th>Date</Th><Th>Equipment</Th><Th>Cycle</Th><Th>Indicators</Th><Th>Operator</Th><Th></Th>
                </>}
                {tab === 'bmw' && <>
                  <Th>Date</Th><Th>Category</Th><Th>Weight (kg)</Th><Th>Manifest</Th><Th>Handler</Th><Th></Th>
                </>}
                {tab === 'adverse' && <>
                  <Th>Date</Th><Th>Patient</Th><Th>Category</Th><Th>Severity</Th><Th>Status</Th><Th></Th>
                </>}
                {tab === 'equipment' && <>
                  <Th>Equipment</Th><Th>Last Service</Th><Th>Next Due</Th><Th>Status</Th><Th>Location</Th><Th></Th>
                </>}
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it._id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  {tab === 'sterilization' && <>
                    <Td>{fmtDate(it.cycleDate)}</Td>
                    <Td>{it.equipmentName}</Td>
                    <Td>{it.cycleType}</Td>
                    <Td><IndicatorPills it={it}/></Td>
                    <Td>{it.operator || '—'}</Td>
                  </>}
                  {tab === 'bmw' && <>
                    <Td>{fmtDate(it.collectionDate)}</Td>
                    <Td><CategoryPill cat={it.category}/></Td>
                    <Td>{it.weightKg}</Td>
                    <Td>{it.manifestNo || '—'}</Td>
                    <Td>{it.handlerName || '—'}</Td>
                  </>}
                  {tab === 'adverse' && <>
                    <Td>{fmtDate(it.eventDate)}</Td>
                    <Td>{it.patientName || '—'}</Td>
                    <Td>{it.category?.replace(/_/g, ' ')}</Td>
                    <Td><SeverityPill s={it.severity}/></Td>
                    <Td><StatusPill s={it.status}/></Td>
                  </>}
                  {tab === 'equipment' && <>
                    <Td>{it.name}</Td>
                    <Td>{fmtDate(it.lastServicedAt)}</Td>
                    <Td><DueCell d={it.nextServiceDueAt}/></Td>
                    <Td>{it.status}</Td>
                    <Td>{it.location || '—'}</Td>
                  </>}
                  <Td>
                    <button onClick={() => remove(it._id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: theme.danger }}>
                      <Trash2 size={14}/>
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Helpers ──
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

const Kpi = ({ label, value, accent, icon: Ic }) => (
  <div style={{ ...cardStyle, padding: 12 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
      <Ic size={14} color={accent}/>
      <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>{label}</span>
    </div>
    <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--color-text)' }}>{value}</div>
  </div>
);

const Th = ({ children }) => (
  <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 11, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>{children}</th>
);
const Td = ({ children }) => (
  <td style={{ padding: '8px 6px', color: 'var(--color-text)' }}>{children}</td>
);

const Pill = ({ text, color }) => (
  <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: color + '22', color, textTransform: 'capitalize' }}>{text}</span>
);

const IndicatorPills = ({ it }) => (
  <span style={{ display: 'inline-flex', gap: 4 }}>
    <Pill text={`Chem: ${it.chemicalIndicator}`} color={it.chemicalIndicator === 'pass' ? theme.success : theme.danger}/>
    {it.biologicalIndicator !== 'na' && <Pill text={`Bio: ${it.biologicalIndicator}`} color={it.biologicalIndicator === 'pass' ? theme.success : theme.danger}/>}
  </span>
);

const CategoryPill = ({ cat }) => {
  const colors = { yellow: '#eab308', red: '#dc2626', white: '#64748b', blue: '#2563eb' };
  return <Pill text={cat} color={colors[cat] || '#666'}/>;
};
const SeverityPill = ({ s }) => {
  const colors = { low: theme.success, moderate: theme.warning, high: theme.danger, sentinel: '#7c2d12' };
  return <Pill text={s} color={colors[s] || '#666'}/>;
};
const StatusPill = ({ s }) => {
  const colors = { open: theme.danger, under_review: theme.warning, closed: theme.success };
  return <Pill text={(s || '').replace(/_/g, ' ')} color={colors[s] || '#666'}/>;
};
const DueCell = ({ d }) => {
  if (!d) return <span style={{ color: 'var(--color-text-tertiary)' }}>—</span>;
  const days = Math.round((new Date(d) - Date.now()) / 86400000);
  const overdue = days < 0;
  const soon = days >= 0 && days <= 7;
  return <Pill text={`${overdue ? 'OVERDUE ' + Math.abs(days) + 'd' : days + 'd'}`} color={overdue ? theme.danger : soon ? theme.warning : theme.success}/>;
};

// ── Forms ──
const Field = ({ label, children }) => (
  <div>
    <label style={{ ...labelStyle, color: 'var(--color-text-secondary)' }}>{label}</label>
    {children}
  </div>
);

const SterilizationForm = ({ form, setForm }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
    <Field label="Equipment Name *">
      <input style={inputStyle} value={form.equipmentName || ''} onChange={e => setForm({ ...form, equipmentName: e.target.value })}/>
    </Field>
    <Field label="Cycle Type">
      <select style={inputStyle} value={form.cycleType || 'wrapped'} onChange={e => setForm({ ...form, cycleType: e.target.value })}>
        {['unwrapped','wrapped','liquid','prion','flash','other'].map(o => <option key={o}>{o}</option>)}
      </select>
    </Field>
    <Field label="Temperature °C">
      <input type="number" style={inputStyle} value={form.temperatureC || ''} onChange={e => setForm({ ...form, temperatureC: Number(e.target.value) })}/>
    </Field>
    <Field label="Duration (min)">
      <input type="number" style={inputStyle} value={form.durationMin || ''} onChange={e => setForm({ ...form, durationMin: Number(e.target.value) })}/>
    </Field>
    <Field label="Chemical Indicator">
      <select style={inputStyle} value={form.chemicalIndicator || 'pass'} onChange={e => setForm({ ...form, chemicalIndicator: e.target.value })}>
        {['pass','fail','na'].map(o => <option key={o}>{o}</option>)}
      </select>
    </Field>
    <Field label="Biological Indicator">
      <select style={inputStyle} value={form.biologicalIndicator || 'na'} onChange={e => setForm({ ...form, biologicalIndicator: e.target.value })}>
        {['pass','fail','pending','na'].map(o => <option key={o}>{o}</option>)}
      </select>
    </Field>
    <Field label="Operator">
      <input style={inputStyle} value={form.operator || ''} onChange={e => setForm({ ...form, operator: e.target.value })}/>
    </Field>
    <Field label="Load Description">
      <input style={inputStyle} value={form.loadDescription || ''} onChange={e => setForm({ ...form, loadDescription: e.target.value })}/>
    </Field>
  </div>
);

const BmwForm = ({ form, setForm }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
    <Field label="Category *">
      <select style={inputStyle} value={form.category || 'yellow'} onChange={e => setForm({ ...form, category: e.target.value })}>
        {['yellow','red','white','blue'].map(o => <option key={o}>{o}</option>)}
      </select>
    </Field>
    <Field label="Weight (kg) *">
      <input type="number" step="0.01" style={inputStyle} value={form.weightKg || ''} onChange={e => setForm({ ...form, weightKg: Number(e.target.value) })}/>
    </Field>
    <Field label="Container Count">
      <input type="number" style={inputStyle} value={form.containerCount || 1} onChange={e => setForm({ ...form, containerCount: Number(e.target.value) })}/>
    </Field>
    <Field label="Manifest No.">
      <input style={inputStyle} value={form.manifestNo || ''} onChange={e => setForm({ ...form, manifestNo: e.target.value })}/>
    </Field>
    <Field label="Handler / Vendor">
      <input style={inputStyle} value={form.handlerName || ''} onChange={e => setForm({ ...form, handlerName: e.target.value })}/>
    </Field>
    <Field label="Vehicle No.">
      <input style={inputStyle} value={form.vehicleNo || ''} onChange={e => setForm({ ...form, vehicleNo: e.target.value })}/>
    </Field>
    <Field label="Handed Over By">
      <input style={inputStyle} value={form.collectedBy || ''} onChange={e => setForm({ ...form, collectedBy: e.target.value })}/>
    </Field>
    <Field label="Notes">
      <input style={inputStyle} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })}/>
    </Field>
  </div>
);

const AdverseForm = ({ form, setForm }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
    <Field label="Patient Name">
      <input style={inputStyle} value={form.patientName || ''} onChange={e => setForm({ ...form, patientName: e.target.value })}/>
    </Field>
    <Field label="Category *">
      <select style={inputStyle} value={form.category || 'medication_error'} onChange={e => setForm({ ...form, category: e.target.value })}>
        {['medication_error','allergic_reaction','fall','equipment_failure','wrong_site','documentation_error','consent_issue','infection','sentinel','near_miss','patient_complaint','other'].map(o => <option key={o}>{o.replace(/_/g, ' ')}</option>)}
      </select>
    </Field>
    <Field label="Severity">
      <select style={inputStyle} value={form.severity || 'low'} onChange={e => setForm({ ...form, severity: e.target.value })}>
        {['low','moderate','high','sentinel'].map(o => <option key={o}>{o}</option>)}
      </select>
    </Field>
    <Field label="Reported By">
      <input style={inputStyle} value={form.reportedBy || ''} onChange={e => setForm({ ...form, reportedBy: e.target.value })}/>
    </Field>
    <div style={{ gridColumn: 'span 2' }}>
      <Field label="Description *">
        <textarea style={{ ...inputStyle, minHeight: 70 }} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })}/>
      </Field>
    </div>
    <div style={{ gridColumn: 'span 2' }}>
      <Field label="Immediate Action Taken">
        <textarea style={{ ...inputStyle, minHeight: 60 }} value={form.immediateAction || ''} onChange={e => setForm({ ...form, immediateAction: e.target.value })}/>
      </Field>
    </div>
  </div>
);

const EquipmentForm = ({ form, setForm }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
    <Field label="Equipment Name *">
      <input style={inputStyle} value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })}/>
    </Field>
    <Field label="Category">
      <input style={inputStyle} placeholder="sterilizer / imaging / chair" value={form.category || ''} onChange={e => setForm({ ...form, category: e.target.value })}/>
    </Field>
    <Field label="Manufacturer">
      <input style={inputStyle} value={form.manufacturer || ''} onChange={e => setForm({ ...form, manufacturer: e.target.value })}/>
    </Field>
    <Field label="Model No.">
      <input style={inputStyle} value={form.modelNo || ''} onChange={e => setForm({ ...form, modelNo: e.target.value })}/>
    </Field>
    <Field label="Service Interval (days)">
      <input type="number" style={inputStyle} value={form.serviceIntervalDays || 180} onChange={e => setForm({ ...form, serviceIntervalDays: Number(e.target.value) })}/>
    </Field>
    <Field label="Last Serviced">
      <input type="date" style={inputStyle} value={form.lastServicedAt ? new Date(form.lastServicedAt).toISOString().slice(0, 10) : ''} onChange={e => setForm({ ...form, lastServicedAt: e.target.value })}/>
    </Field>
    <Field label="Location">
      <input style={inputStyle} value={form.location || ''} onChange={e => setForm({ ...form, location: e.target.value })}/>
    </Field>
    <Field label="Status">
      <select style={inputStyle} value={form.status || 'active'} onChange={e => setForm({ ...form, status: e.target.value })}>
        {['active','out_of_service','retired'].map(o => <option key={o}>{o}</option>)}
      </select>
    </Field>
  </div>
);
